import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { Save, Download, Trash2, FileText } from 'lucide-react';

interface SavedCalculation {
  id: string;
  type: string;
  inputs: Record<string, any>;
  result: string;
  name: string | null;
  created_at: string;
}

interface SavedCalculationsProps {
  type: string;
  inputs: Record<string, any>;
  result: string;
}

export const SavedCalculations: React.FC<SavedCalculationsProps> = ({ type, inputs, result }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [savedCalculations, setSavedCalculations] = useState<SavedCalculation[]>([]);
  const [saving, setSaving] = useState(false);
  const [calculationName, setCalculationName] = useState('');

  useEffect(() => {
    if (user) {
      fetchSavedCalculations();
    }
  }, [user]);

  const fetchSavedCalculations = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('saved_calculations')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setSavedCalculations((data || []).map(item => ({
        ...item,
        inputs: item.inputs as Record<string, any>
      })));
    } catch (error: any) {
      console.error('Error fetching saved calculations:', error);
    }
  };

  const saveCalculation = async () => {
    if (!user || !result) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('saved_calculations')
        .insert({
          user_id: user.id,
          type,
          inputs,
          result,
          name: calculationName || null,
        });

      if (error) throw error;

      toast({
        title: "Calculation saved!",
        description: "Your calculation has been saved successfully.",
      });

      setCalculationName('');
      fetchSavedCalculations();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteCalculation = async (id: string) => {
    try {
      const { error } = await supabase
        .from('saved_calculations')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Calculation deleted",
        description: "The calculation has been removed.",
      });

      fetchSavedCalculations();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const sanitizeForCSV = (value: string): string => {
    // Security Fix: Prevent CSV formula injection
    if (typeof value !== 'string') return String(value);
    
    // If value starts with formula indicators, prefix with single quote
    if (value.startsWith('=') || value.startsWith('+') || value.startsWith('-') || value.startsWith('@')) {
      return `'${value}`;
    }
    
    return value;
  };

  const exportCalculations = () => {
    const csvContent = [
      ['Name', 'Type', 'Result', 'Date'],
      ...savedCalculations.map(calc => [
        sanitizeForCSV(calc.name || 'Unnamed'),
        sanitizeForCSV(calc.type),
        sanitizeForCSV(calc.result),
        sanitizeForCSV(new Date(calc.created_at).toLocaleDateString())
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'saved_calculations.csv';
    a.click();
    window.URL.revokeObjectURL(url);

    toast({
      title: "Export complete",
      description: "Your calculations have been exported to CSV.",
    });
  };

  if (!user) return null;

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Save className="w-5 h-5" />
          Save & Export
        </CardTitle>
        <CardDescription>
          Save this calculation and manage your saved results
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {result && (
          <div className="flex gap-2">
            <Input
              placeholder="Name this calculation (optional)"
              value={calculationName}
              onChange={(e) => setCalculationName(e.target.value)}
              className="flex-1"
            />
            <Button onClick={saveCalculation} disabled={saving} size="sm">
              <Save className="w-4 h-4" />
            </Button>
          </div>
        )}

        {savedCalculations.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-sm">Recent Calculations</h4>
              <Button onClick={exportCalculations} variant="outline" size="sm">
                <Download className="w-4 h-4 mr-1" />
                Export CSV
              </Button>
            </div>
            
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {savedCalculations.map((calc) => (
                <div key={calc.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium text-sm">
                        {calc.name || 'Unnamed'}
                      </span>
                      <Badge variant="secondary" className="text-xs">
                        {calc.type}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">{calc.result}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(calc.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Button
                    onClick={() => deleteCalculation(calc.id)}
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};